import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { fetchQuickAccess, saveQuickAccess } from './api.js';
import {
  itemKey,
  mergeItems,
  readFavorites,
  readRecents,
  writeFavorites,
  writeRecents,
  FAVORITES_LIMIT,
  RECENTS_LIMIT,
  type FavoriteInput,
  type FavoriteItem,
  type QuickAccessMenu,
  type QuickAccessRecord,
  type RecentItem,
} from './store.js';

interface QuickAccessContextValue {
  recents: RecentItem[];
  favorites: FavoriteItem[];
  /** Records a record view/edit at the top of the recents list. */
  trackRecent: (rec: Omit<QuickAccessRecord, 'kind' | 'at'>) => void;
  /** Pins/unpins a record or menu item. */
  toggleFavorite: (item: FavoriteInput) => void;
  isFavorite: (key: string) => boolean;
  removeFavorite: (key: string) => void;
  /** Moves the favorite with `fromKey` to the position currently held by `toKey`. */
  moveFavorite: (fromKey: string, toKey: string) => void;
  removeRecent: (key: string) => void;
  clearRecents: () => void;
}

const QuickAccessContext = createContext<QuickAccessContextValue | null>(null);

const SAVE_DEBOUNCE_MS = 700;
const RELOAD_BASE_DELAY_MS = 1_000;
const RELOAD_MAX_DELAY_MS = 30_000;

/**
 * Provides the current member's "Recent" and "Favorites" quick-access lists. The
 * authoritative copy is stored in the database (per member, so it persists across devices
 * and sessions); a localStorage cache paints instantly on load and each change is written
 * back to the server (debounced). Must sit inside MsalProvider so the API client can
 * acquire a token.
 *
 * Saving replaces the member's whole server row, so it is deliberately gated on a
 * *successful* initial load: until we know what the server holds, a local list built from
 * an empty cache must never be written back, or one failed request would wipe the member's
 * favorites on every other device.
 */
export function QuickAccessProvider({ children }: { children: ReactNode }): JSX.Element {
  const [recents, setRecents] = useState<RecentItem[]>(() => readRecents());
  const [favorites, setFavorites] = useState<FavoriteItem[]>(() => readFavorites());
  /** True once the server copy has been read at least once; gates all writes. */
  const [hydrated, setHydrated] = useState(false);
  const skipNextSaveRef = useRef(false);
  /** Set when the user edits a list before the server copy lands, so we merge instead of replace. */
  const dirtyBeforeHydrationRef = useRef(false);

  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  // The API client returns no token until MSAL has settled on an account; requesting
  // earlier just yields a 401 that would otherwise be mistaken for "no saved data".
  const authReady = isAuthenticated && inProgress === InteractionStatus.None;

  // Load the authoritative server copy, retrying with backoff. `hydrated` flips only on a
  // real response — a failure leaves saving disabled rather than risking a clobber.
  useEffect(() => {
    if (!authReady || hydrated) return undefined;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = (): void => {
      void fetchQuickAccess().then((state) => {
        if (cancelled) return;
        if (!state) {
          attempt += 1;
          timer = setTimeout(load, Math.min(RELOAD_MAX_DELAY_MS, RELOAD_BASE_DELAY_MS * 2 ** attempt));
          return;
        }
        if (dirtyBeforeHydrationRef.current) {
          // Keep edits made while the request was in flight; the merged result is saved.
          setRecents((local) => mergeItems(local, state.recents, RECENTS_LIMIT));
          setFavorites((local) => mergeItems(local, state.favorites, FAVORITES_LIMIT));
        } else {
          // Nothing changed locally, so take the server copy verbatim (this is what lets a
          // removal made on another device stick) and don't echo it straight back.
          skipNextSaveRef.current = true;
          setRecents(state.recents);
          setFavorites(state.favorites);
        }
        setHydrated(true);
      });
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [authReady, hydrated]);

  // Mirror every change to the localStorage cache immediately, and persist to the server
  // (debounced) once the initial server load has succeeded.
  const pendingSaveRef = useRef<{ recents: RecentItem[]; favorites: FavoriteItem[] } | null>(null);
  useEffect(() => {
    writeRecents(recents);
    writeFavorites(favorites);
    if (!hydrated) return undefined;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return undefined;
    }
    pendingSaveRef.current = { recents, favorites };
    const timer = setTimeout(() => {
      void saveQuickAccess({ recents, favorites }).then((ok) => {
        if (ok) pendingSaveRef.current = null;
      });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [recents, favorites, hydrated]);

  // A change made moments before the tab is closed or hidden would otherwise die with the
  // debounce timer; flush it with a keepalive request instead.
  useEffect(() => {
    const flush = (): void => {
      const pending = pendingSaveRef.current;
      if (!pending) return;
      pendingSaveRef.current = null;
      void saveQuickAccess(pending, { keepalive: true });
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  /** Flags a pre-hydration edit so the pending server response merges instead of replaces. */
  const hydratedRef = useRef(false);
  hydratedRef.current = hydrated;
  const markDirty = useCallback((): void => {
    if (!hydratedRef.current) dirtyBeforeHydrationRef.current = true;
  }, []);

  const trackRecent = useCallback(
    (rec: Omit<QuickAccessRecord, 'kind' | 'at'>): void => {
      markDirty();
      setRecents((prev) => {
        const item: RecentItem = { kind: 'record', at: Date.now(), ...rec };
        const key = itemKey(item);
        return [item, ...prev.filter((r) => itemKey(r) !== key)].slice(0, RECENTS_LIMIT);
      });
    },
    [markDirty],
  );

  const toggleFavorite = useCallback(
    (input: FavoriteInput): void => {
      markDirty();
      setFavorites((prev) => {
        const key = itemKey({ ...input, at: 0 } as FavoriteItem);
        const exists = prev.some((f) => itemKey(f) === key);
        return exists
          ? prev.filter((f) => itemKey(f) !== key)
          : [{ ...input, at: Date.now() } as FavoriteItem, ...prev].slice(0, FAVORITES_LIMIT);
      });
    },
    [markDirty],
  );

  const removeFavorite = useCallback(
    (key: string): void => {
      markDirty();
      setFavorites((prev) => prev.filter((f) => itemKey(f) !== key));
    },
    [markDirty],
  );

  const moveFavorite = useCallback(
    (fromKey: string, toKey: string): void => {
      if (fromKey === toKey) return;
      markDirty();
      setFavorites((prev) => {
        const fromIndex = prev.findIndex((f) => itemKey(f) === fromKey);
        const toIndex = prev.findIndex((f) => itemKey(f) === toKey);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved!);
        return next;
      });
    },
    [markDirty],
  );

  const removeRecent = useCallback(
    (key: string): void => {
      markDirty();
      setRecents((prev) => prev.filter((r) => itemKey(r) !== key));
    },
    [markDirty],
  );

  const clearRecents = useCallback((): void => {
    markDirty();
    setRecents([]);
  }, [markDirty]);

  const favoriteKeys = useMemo(() => new Set(favorites.map((f) => itemKey(f))), [favorites]);
  const isFavorite = useCallback((key: string): boolean => favoriteKeys.has(key), [favoriteKeys]);

  const value = useMemo<QuickAccessContextValue>(
    () => ({
      recents,
      favorites,
      trackRecent,
      toggleFavorite,
      isFavorite,
      removeFavorite,
      moveFavorite,
      removeRecent,
      clearRecents,
    }),
    [
      recents,
      favorites,
      trackRecent,
      toggleFavorite,
      isFavorite,
      removeFavorite,
      moveFavorite,
      removeRecent,
      clearRecents,
    ],
  );

  return <QuickAccessContext.Provider value={value}>{children}</QuickAccessContext.Provider>;
}

export function useQuickAccess(): QuickAccessContextValue {
  const context = useContext(QuickAccessContext);
  if (!context) {
    throw new Error('useQuickAccess must be used within a QuickAccessProvider');
  }
  return context;
}

export type { FavoriteItem, QuickAccessMenu, QuickAccessRecord, RecentItem };
